// src/components/document/DocumentSpacesSidebar.tsx
// Sidebar for navigating document spaces (collections).
// Shows: Recent, Favorites, Spaces (Wiki, Policies, Procedures, etc.)

import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { DocumentSpace, RecentDocument } from '@/features/document/types';
import { getTypeIconPath } from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ labelKey, t }: { labelKey: string; t: (k: string) => string }) {
  return (
    <h4 className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {t(labelKey)}
    </h4>
  );
}

// ---------------------------------------------------------------------------
// Recent documents
// ---------------------------------------------------------------------------

function RecentDocsList({
  items,
  onNavigate,
  t,
}: {
  items: RecentDocument[];
  onNavigate: (id: string) => void;
  t: (k: string) => string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <SectionHeader labelKey="document.spaces.recent" t={t} />
      <div className="space-y-0.5 px-1">
        {items.slice(0, 5).map((doc) => (
          <button
            key={doc.id}
            onClick={() => onNavigate(doc.id)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-muted transition-colors text-left"
          >
            <svg className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={getTypeIconPath(doc.type)} />
            </svg>
            <span className="truncate">{doc.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Favorites
// ---------------------------------------------------------------------------

function FavoriteDocsList({
  items,
  onNavigate,
  t,
}: {
  items: { id: string; title: string; type: string }[];
  onNavigate: (id: string) => void;
  t: (k: string) => string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <SectionHeader labelKey="document.spaces.favorites" t={t} />
      <div className="space-y-0.5 px-1">
        {items.map((doc) => (
          <button
            key={doc.id}
            onClick={() => onNavigate(doc.id)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-muted transition-colors text-left"
          >
            <svg className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            <span className="truncate">{doc.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spaces list
// ---------------------------------------------------------------------------

function SpacesList({
  spaces,
  activeSpaceId,
  onSelectSpace,
  t,
}: {
  spaces: DocumentSpace[];
  activeSpaceId: string;
  onSelectSpace: (id: string) => void;
  t: (k: string) => string;
}) {
  // Space icon map
  const iconPaths: Record<string, string> = {
    folder: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
    'book-open': 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  };

  return (
    <div>
      <SectionHeader labelKey="document.spaces.title" t={t} />
      <div className="space-y-0.5 px-1">
        {spaces.map((space) => {
          const isActive = space.id === activeSpaceId;
          return (
            <button
              key={space.id}
              onClick={() => onSelectSpace(space.id)}
              className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors text-left ${
                isActive
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              <svg
                className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[space.iconKey] || iconPaths.folder} />
              </svg>
              <span className="truncate flex-1">{t(space.title)}</span>
              <Badge variant="secondary" size="sm">{space.docCount}</Badge>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main sidebar component
// ---------------------------------------------------------------------------

interface DocumentSpacesSidebarProps {
  spaces: DocumentSpace[];
  activeSpaceId: string;
  onSelectSpace: (id: string) => void;
  recentDocs: RecentDocument[];
  favoriteDocs: { id: string; title: string; type: string }[];
  onNavigateToDoc: (id: string) => void;
  onCreateDaily?: () => void;
}

export function DocumentSpacesSidebar({
  spaces,
  activeSpaceId,
  onSelectSpace,
  recentDocs,
  favoriteDocs,
  onNavigateToDoc,
  onCreateDaily,
}: DocumentSpacesSidebarProps) {
  const { t } = useI18n();

  return (
    <div className="w-[220px] flex-shrink-0 border-r border-border bg-sidebar overflow-y-auto">
      {/* Daily note button */}
      {onCreateDaily && (
        <div className="p-2 border-b border-border">
          <button
            onClick={onCreateDaily}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground bg-muted/50 hover:bg-muted transition-colors"
          >
            <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM16 2v4M8 2v4M3 10h18" />
            </svg>
            {t('document.dailyNote')}
          </button>
        </div>
      )}

      <RecentDocsList items={recentDocs} onNavigate={onNavigateToDoc} t={t} />
      <FavoriteDocsList items={favoriteDocs} onNavigate={onNavigateToDoc} t={t} />
      <SpacesList spaces={spaces} activeSpaceId={activeSpaceId} onSelectSpace={onSelectSpace} t={t} />
    </div>
  );
}
